"use client";

import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IQueryTransactionsRequest, ITransactionResponse } from "@finbackoffice/clientbff-client";
import { CurrencyShortNames } from "@finbackoffice/fe-core";
import {
    AssetsDirectory,
    TransactionSource,
    TranslationScopes,
    WalletType,
} from "@finbackoffice/enums";
import { ClientBFFContext, DEFAULT_PAGINATION, DropdownOption } from "@finbackoffice/site-core";
import { WalletsContext } from "contexts";
import { format } from "date-fns";
import Loading from "components/base/loading/Loading";
import Pagination from "components/base/pagination/Pagination";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import CustomSelect from "components/base/custom-select/CustomSelect";
import { useDate } from "hooks";
import { ModalTypes } from "utils/constants";
import NoData from "components/base/no-data/NoData";
import AccountHeader from "../header/AccountHeader";
import TransactionsTools from "./tools/TransactionsTools";
import TransactionRow from "./transaction-row/TransactionRow";
import styles from "./account-transactions.module.sass";

type ITransactionsState = {
    transactions: ITransactionResponse[];
    loading: boolean;
};

export type ITransactionsFilterState = {
    wallet_id: string[];
    operation: string;
    source: string;
    from: Date | null;
    to: Date | null;
};

function convertToNumbers(arr: string[]): number[] {
    const result: number[] = [];

    for (let i = 0; i < arr.length; i++) {
        const itemArr = arr[i].split(",");
        for (let j = 0; j < itemArr.length; j++) {
            if (!Number.isNaN(parseInt(itemArr[j]))) {
                result.push(parseInt(itemArr[j]));
            }
        }
    }

    return result;
}

function getFilterQuery(filter: ITransactionsFilterState): Partial<IQueryTransactionsRequest> {
    const filterQuery: Partial<IQueryTransactionsRequest> = {};

    if (filter.from) {
        filterQuery.from = format(filter.from, "yyyy-MM-dd HH:mm:ss");
    }

    if (filter.to) {
        filterQuery.to = format(filter.to, "yyyy-MM-dd HH:mm:ss");
    }

    if (filter.operation) {
        const [source, operation] = filter.operation.split("-");
        filterQuery.operation = operation.split(",");
        filterQuery.source = source as TransactionSource;
    }

    if (filter.wallet_id[0] !== "") {
        filterQuery.wallet_id = convertToNumbers(filter.wallet_id);
    }

    return filterQuery;
}

type IProps = {
    isPageMode?: boolean;
};

const AccountTransactions: FC<IProps> = ({ isPageMode = false }) => {
    const { startOfDay, subDays, endOfDay } = useDate();
    const client = useContext(ClientBFFContext);
    const { siteSupportedWallets } = useContext(WalletsContext);

    const [transactionsState, setTransactionsState] = useState<ITransactionsState>({
        transactions: [],
        loading: false,
    });
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [filter, setFilter] = useState<ITransactionsFilterState>({
        wallet_id: [""],
        operation: "",
        from: subDays(startOfDay(new Date()), 15),
        to: endOfDay(new Date()),
        source: "",
    });

    const handleFilterChange = useCallback((updatedFilter: ITransactionsFilterState) => {
        setFilter(updatedFilter);
        setPagination((state) => ({
            ...state,
            page: 1,
        }));
    }, []);

    const onPageChangeHandler = (page: number) => {
        setPagination((state) => ({
            ...state,
            page,
        }));
    };

    const onPerPageHandler = (val: string) => {
        setPagination((state) => ({
            ...state,
            items_per_page: parseInt(val),
            page: 1,
        }));
    };

    useEffect(() => {
        let isMounted = true;
        const loadTransactions = async () => {
            setTransactionsState((state) => ({
                ...state,
                loading: true,
            }));
            try {
                const response = await client.queryTransaction({
                    page: pagination.page,
                    items_per_page: pagination.items_per_page,
                    /* order_by: [
                        {
                            field: "bet_settled_at",
                            order: Order.DESC
                        }
                    ], */
                    ...getFilterQuery(filter),
                });

                if (isMounted) {
                    setTransactionsState((state) => ({
                        ...state,
                        loading: false,
                        transactions: response.items,
                    }));
                    setPagination((state) => ({
                        ...state,
                        total_pages: response.total_pages,
                    }));
                }
            } catch (_error: unknown) {
                if (isMounted) {
                    setTransactionsState((state) => ({
                        ...state,
                        loading: false,
                    }));
                }
            }
        };

        void loadTransactions();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, pagination.items_per_page, pagination.page]);

    const currencies: DropdownOption[] = useMemo(() => {
        const result: DropdownOption[] = [];

        siteSupportedWallets?.forEach((wallet) => {
            if ("id" in wallet && wallet.currency) {
                result.push({
                    value: wallet.id.toString(),
                    label: `${(CurrencyShortNames as any)[wallet.currency] || wallet.currency}${
                        wallet.type === WalletType.Bonus ? " - Bonus" : ""
                    }`,
                    svgSrc: `/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${wallet.currency.toLowerCase()}.svg`,
                });
            }
        });

        result.unshift({
            value: "",
            label: {
                tid: "account_transactions_currency_all",
                namespace: TranslationScopes.UserAccount,
            },
        });

        return result;
    }, [siteSupportedWallets]);

    return (
        <div className={styles.balanceHistory} data-testid="account-transactions-content">
            {isPageMode ? (
                <div className={styles.tabletHeader}>
                    <CustomSelect
                        data={currencies}
                        selected={filter.wallet_id}
                        onChange={(val: string[]) =>
                            handleFilterChange({
                                ...filter,
                                wallet_id: val[0] ? (val as string[]) : [""],
                            })
                        }
                        wrapperClassname={styles.currencyType}
                        type={ModalTypes.MULTIPLE_SELECTOR_CURRENCY}
                        multiple
                        loading={!siteSupportedWallets}
                        variantStyle="gradient"
                    />
                    <TransactionsTools onChange={handleFilterChange} filter={filter} />
                </div>
            ) : (
                <>
                    <AccountHeader
                        className={styles.header}
                        headingIcon={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-balance.svg`}
                        tId="account_transactions_heading">
                        <CustomSelect
                            data={currencies}
                            selected={filter.wallet_id}
                            onChange={(val: string[]) =>
                                handleFilterChange({
                                    ...filter,
                                    wallet_id: val[0] ? (val as string[]) : [""],
                                })
                            }
                            wrapperClassname={styles.currencyType}
                            type={ModalTypes.MULTIPLE_SELECTOR_CURRENCY}
                            multiple
                            loading={!siteSupportedWallets}
                            variantStyle="gradient"
                        />
                    </AccountHeader>
                    <TransactionsTools onChange={handleFilterChange} filter={filter} />
                </>
            )}
            <FadeInAnimation>
                <div className={styles.content}>
                    {transactionsState.loading && <Loading />}
                    {!transactionsState.loading && (
                        <>
                            {transactionsState.transactions?.map((transaction) => (
                                <TransactionRow key={transaction.id} transaction={transaction} />
                            ))}
                            {transactionsState.transactions &&
                                transactionsState.transactions.length === 0 && <NoData />}
                        </>
                    )}
                </div>
            </FadeInAnimation>
            {transactionsState.transactions && !!transactionsState.transactions.length && (
                <Pagination
                    items_per_page={pagination.items_per_page}
                    page={pagination.page}
                    total_pages={pagination.total_pages || 0}
                    onPageChange={onPageChangeHandler}
                    onPerPage={onPerPageHandler}
                />
            )}
        </div>
    );
};

export default AccountTransactions;
